import { useCallback, useEffect, useState } from "react";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function LetterStatusTable({ running, onChanged }) {
  const [rows, setRows] = useState([]);
  const [only, setOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/letters/access/email/status").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, running]);

  const n = rows.filter((r) => r.can_remind).length;
  const remind = async () => {
    if (!window.confirm(`Inviare un'email di sollecito a ${n} clienti che non hanno ancora effettuato l'accesso?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/letters/access/email/remind"); toast.success(`Solleciti avviati: ${data.total}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); load(); }
  };
  const shown = only ? rows.filter((r) => r.to_remind) : rows;

  return (
    <div className="space-y-2" data-testid="letters-status-section">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-heading font-semibold text-sm text-slate-900">Primo accesso dei clienti</p>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={only} onChange={(e) => setOnly(e.target.checked)} data-testid="letters-status-filter" />Da sollecitare ({rows.filter((r) => r.to_remind).length})
        </label>
        <Button size="sm" variant="outline" className="ml-auto" disabled={busy || !!running || n === 0} onClick={remind} data-testid="letters-remind-button">
          <BellRing className="h-4 w-4 mr-1" />Invia sollecito ({n})
        </Button>
      </div>
      <div className="max-h-56 overflow-y-auto border border-slate-100 rounded-md">
        <table className="w-full text-xs" data-testid="letters-status-table">
          <thead className="bg-slate-50 text-slate-500 sticky top-0">
            <tr><th className="text-left px-2 py-1.5">Cliente</th><th className="text-left px-2 py-1.5">Lettera</th><th className="text-left px-2 py-1.5">Primo accesso</th><th className="text-left px-2 py-1.5">Ultimo sollecito</th></tr>
          </thead>
          <tbody>
            {!shown.length && <tr><td colSpan={4} className="px-2 py-4 text-center text-slate-400">Nessun cliente</td></tr>}
            {shown.map((r) => (
              <tr key={r.email} className="border-t border-slate-50" data-testid={`letters-status-row-${r.email}`}>
                <td className="px-2 py-1.5">{r.name}<span className="block text-slate-400">{r.email}</span></td>
                <td className="px-2 py-1.5">{r.letter_sent_at ? fmtDateTime(r.letter_sent_at) : <span className="text-slate-400">non inviata</span>}</td>
                <td className="px-2 py-1.5">{r.first_login_at ? <span className="text-emerald-700 font-medium">{fmtDateTime(r.first_login_at)}</span> : <span className="text-amber-700 font-medium">non ancora</span>}</td>
                <td className="px-2 py-1.5 text-slate-500">
                  {r.last_reminder_at ? `${fmtDateTime(r.last_reminder_at)} (${r.reminders_count}/3)` : "—"}
                  {r.call_needed && <span className="block text-red-700 font-medium" data-testid={`letters-call-needed-${r.email}`}>da contattare a voce</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">Il sollecito parte solo verso chi ha ricevuto la lettera e non è ancora entrato, al massimo una volta ogni 7 giorni e non oltre 3 volte. Ogni lunedì alle 9:00 parte anche in automatico (sospendibile in Collegamenti tecnici).</p>
    </div>
  );
}
